import { adultAgeOf, isMinor } from './builtin';
import { localTodayIso } from './dates';
import { pdfRequiredSigners } from './pdfSigners';
import { canonicalJson as canonical } from './canonicalJson';
import { isDocumentCompletion, readPdfRevisionProgress } from './pdfProgress';
import { activeQuestionAnswers } from './questionRouting';
import { resolvePdfBindings, validatePdfQuestionAnswer } from './pdfBindings';
import { validQuestionItemIds } from './questionIds';
import type { BuiltinAnswers, CustomAnswer, DocumentCompletion, Field, PdfDocumentProgress, PdfRevisionProgress, PublishedEventRevision } from './types';

const own = <T>(values: Record<string, T>, key: string): T | undefined => Object.hasOwn(values, key) ? values[key] : undefined;
const unique = (labels: string[]) => new Set(labels.map(label => label.trim().toLowerCase())).size === labels.length;
const family = (type: Field['type']) => ['short_text', 'paragraph'].includes(type) ? 'text' : ['choice', 'dropdown'].includes(type) ? 'choice' : type;

/** Resolve a legacy label through its collecting definition. When both definitions have IDs,
 * removed/recreated options must never inherit the old answer just because their labels match.
 */
function translateLabel(label: string, oldLabels: string[], oldIds: string[] | undefined, nextLabels: string[], nextIds: string[] | undefined): string | undefined {
  if (!unique(oldLabels) || !unique(nextLabels)) return undefined;
  const index = oldLabels.indexOf(label);
  if (index < 0) return undefined;
  if (!validQuestionItemIds(oldIds, oldLabels.length) || !validQuestionItemIds(nextIds, nextLabels.length)) return undefined;
  if (oldIds && nextIds) {
    const target = nextIds.indexOf(oldIds[index]);
    return target < 0 ? undefined : nextLabels[target];
  }
  // One-time legacy migration can retain an exact unambiguous label, without inventing IDs on read.
  return nextLabels.includes(label) ? label : undefined;
}

function recoverAnswer(before: Field, after: Field, answer: CustomAnswer): CustomAnswer | undefined {
  if (family(before.type) !== family(after.type) || validatePdfQuestionAnswer(before, answer)) return undefined;
  if (before.type === 'date' && ((before.includeYear !== false) !== (after.includeYear !== false) || !!before.includeTime !== !!after.includeTime)) return undefined;
  if (before.type === 'time' && (before.timeMode ?? 'time') !== (after.timeMode ?? 'time')) return undefined;
  const option = (label: string): string | undefined => {
    if (before.options?.includes(label)) return translateLabel(label, before.options, before.optionIds, after.options ?? [], after.optionIds);
    // Other remains Other, and may not silently become a new configured option.
    return before.type !== 'dropdown' && before.allowOther && after.type !== 'dropdown' && after.allowOther && !after.options?.includes(label) ? label : undefined;
  };
  let next: CustomAnswer | undefined = answer;
  if (before.type === 'choice' || before.type === 'dropdown') next = typeof answer === 'string' ? option(answer) : undefined;
  if (before.type === 'checkboxes') next = Array.isArray(answer) ? answer.flatMap(label => { const value = option(label); return value === undefined ? [] : [value]; }) : undefined;
  if (before.type === 'choice_grid' || before.type === 'checkbox_grid') {
    const oldGrid = before.grid, newGrid = after.grid;
    if (!oldGrid || !newGrid || !unique(oldGrid.rows) || !unique(oldGrid.columns) || !unique(newGrid.rows) || !unique(newGrid.columns) || !answer || typeof answer !== 'object' || Array.isArray(answer)) return undefined;
    const rows: Record<string, string | string[]> = Object.create(null);
    for (const oldRow of oldGrid.rows) {
      const newRow = translateLabel(oldRow, oldGrid.rows, oldGrid.rowIds, newGrid.rows, newGrid.rowIds);
      const selected = own(answer, oldRow);
      if (newRow === undefined || selected === undefined) continue;
      const columns = (Array.isArray(selected) ? selected : [selected]).flatMap(label => {
        const column = translateLabel(label, oldGrid.columns, oldGrid.columnIds, newGrid.columns, newGrid.columnIds);
        return column === undefined ? [] : [column];
      });
      if (columns.length) rows[newRow] = before.type === 'choice_grid' ? columns[0] : columns;
    }
    next = Object.keys(rows).length ? rows : undefined;
  }
  if (Array.isArray(next) && !next.length) return undefined;
  return next !== undefined && !validatePdfQuestionAnswer(after, next) ? structuredClone(next) : undefined;
}

export function recoverRevisionAnswers(before: PublishedEventRevision, after: PublishedEventRevision, answers: Record<string, CustomAnswer>): Record<string, CustomAnswer> {
  if (before.eventId !== after.eventId) throw new Error('Cannot recover answers into a different event');
  const active = activeQuestionAnswers(before.event.fields, answers);
  const recovered: Record<string, CustomAnswer> = Object.create(null);
  for (const field of after.event.fields) {
    const oldFields = before.event.fields.filter(old => old.id === field.id);
    if (oldFields.length !== 1 || after.event.fields.filter(next => next.id === field.id).length !== 1) continue;
    const answer = own(active, field.id);
    if (answer === undefined) continue;
    const next = recoverAnswer(oldFields[0], field, answer);
    if (next !== undefined) recovered[field.id] = next;
  }
  return activeQuestionAnswers(after.event.fields, recovered);
}


function contextContent(revision: PublishedEventRevision, documentId: string, builtin: BuiltinAnswers, custom: Record<string, CustomAnswer>, completion: DocumentCompletion): string | undefined {
  if (!isDocumentCompletion(completion)) return undefined;
  const document = revision.documentForms.find(document => document.id === documentId);
  if (!document || document.id !== completion.documentId || document.templateSha256 !== completion.templateSha256) return undefined;
  const result = resolvePdfBindings({ event: revision.event, document, builtin, custom, documentAnswers: completion.answers });
  if (result.issues.length) return undefined;
  // Signature values are separately serialized below; no payload decoding or PNG trust occurs here.
  return canonical({ eventId: revision.eventId, participantIdentity: { firstName: builtin.firstName, lastName: builtin.lastName, dateOfBirth: builtin.dateOfBirth }, document: { ...document, placements: [...document.placements].sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0) },
    values: result.values, answers: completion.answers, participantSignature: completion.participantSignature, parentSignature: completion.parentSignature,
    confirmedSignatureRoles: [...completion.confirmedSignatureRoles].sort(), signedDates: completion.signedDates,
    // Who signs follows the participant's age on the day of signing: the day this form was signed, or today until it is.
    requiredRoles: pdfRequiredSigners(document, isMinor(builtin.dateOfBirth, completion.signedDates.participant ?? completion.signedDates.parent ?? localTodayIso(), adultAgeOf(revision.event))),
  });
}

/** Browser-local comparison including the revision ID. Never store it in D1/R2/logs/journal metadata.
 * Only explicit recovery between known snapshots can rebind an unchanged document. */
export function pdfDocumentSignatureContext(revision: PublishedEventRevision, builtin: BuiltinAnswers, custom: Record<string, CustomAnswer>, completion: DocumentCompletion): string | undefined {
  const content = contextContent(revision, completion.documentId, builtin, custom, completion);
  return content === undefined ? undefined : canonical({ publishedRevisionId: revision.id, content });
}

export interface RevisionRecoveryInput {
  builtin: BuiltinAnswers;
  custom: Record<string, CustomAnswer>;
  progress: PdfRevisionProgress;
}
export interface RevisionRecoveryResult extends RevisionRecoveryInput {
  invalidatedDocumentIds: string[];
  removedDocumentIds: string[];
  clearedQuestionIds: string[];
  /** Recovery signal; false does not replace the standard form’s existing context check. */
  requiresStandardResign: boolean;
}

/** Pure local recovery. It has no network/storage access and never automatically resubmits.
 * File names are dropped because they do not establish retained File objects; Step 5 must reselect them.
 * Preserve attempt identity for Step 6 reconciliation; a new revision cannot authorize a blind retry.
 */
export function recoverRevisionProgress(before: PublishedEventRevision, after: PublishedEventRevision, previous: RevisionRecoveryInput): RevisionRecoveryResult {
  if (before.eventId !== after.eventId) throw new Error('Cannot recover progress into a different event');
  if (previous.progress.publishedRevisionId !== undefined && previous.progress.publishedRevisionId !== before.id) throw new Error('The collecting revision does not match this progress');
  const savedProgress = readPdfRevisionProgress(previous.progress);
  const builtin = structuredClone(previous.builtin);
  if (after.event.level === 'ward') builtin.ward = after.event.wardName ?? '';
  else if (!after.event.wardNames.includes(builtin.ward)) builtin.ward = '';
  const custom = recoverRevisionAnswers(before, after, previous.custom);
  const documents: Record<string, PdfDocumentProgress> = Object.create(null);
  const invalidatedDocumentIds: string[] = [];
  for (const document of after.documentForms) {
    const oldDocument = before.documentForms.find(old => old.id === document.id);
    const oldProgress = own(savedProgress.documents, document.id);
    // The form's own answers carry over by the same rules as registration answers.
    const answers: Record<string, CustomAnswer> = Object.create(null);
    if (oldDocument && oldProgress && oldProgress.completion.documentId === document.id && oldProgress.completion.templateSha256 === oldDocument.templateSha256) for (const question of document.questions) {
      const oldQuestion = oldDocument.questions.find(old => old.id === question.id);
      const value = own(oldProgress.completion.answers, question.id);
      // Agreeing to a statement on one PDF is not agreeing to it on a replacement PDF.
      const replaced = oldDocument.templateSha256 !== document.templateSha256 || oldDocument.assetId !== document.assetId;
      const recovered = oldQuestion && value !== undefined && !(replaced && question.type === 'acknowledgment') ? recoverAnswer(oldQuestion, question, value) : undefined;
      if (recovered !== undefined) answers[question.id] = recovered;
    }
    const completion: DocumentCompletion = { documentId: document.id, templateSha256: document.templateSha256, answers, reviewed: false, confirmedSignatureRoles: [], signedDates: {} };
    if (oldDocument && oldProgress) {
      const carried = { ...structuredClone(oldProgress.completion), documentId: document.id, templateSha256: document.templateSha256, answers };
      const oldContent = contextContent(before, document.id, previous.builtin, previous.custom, oldProgress.completion);
      const newContent = contextContent(after, document.id, builtin, custom, carried);
      const verifiedContext = pdfDocumentSignatureContext(before, previous.builtin, previous.custom, oldProgress.completion);
      const unchanged = savedProgress.publishedRevisionId === before.id && verifiedContext !== undefined && oldProgress.signatureContext === verifiedContext && oldContent !== undefined && oldContent === newContent;
      if (unchanged) {
        documents[document.id] = { completion: carried, signatureContext: pdfDocumentSignatureContext(after, builtin, custom, carried) };
        continue;
      }
    }
    documents[document.id] = { completion };
    invalidatedDocumentIds.push(document.id);
  }
  const removedDocumentIds = Object.keys(previous.progress.documents).filter(id => !after.documentForms.some(document => document.id === id));
  const clearedQuestionIds = Object.keys(previous.custom).filter(id => !Object.hasOwn(custom, id));
  const changed = before.id !== after.id || canonical(previous.builtin) !== canonical(builtin) || canonical(previous.custom) !== canonical(custom);
  const activeDocumentId = previous.progress.activeDocumentId && Object.hasOwn(documents, previous.progress.activeDocumentId) ? previous.progress.activeDocumentId : invalidatedDocumentIds[0] ?? after.documentForms[0]?.id;
  return { builtin, custom, progress: { schemaVersion: 1, publishedRevisionId: after.id, documents, ...(activeDocumentId ? { activeDocumentId } : {}), ...(savedProgress.submissionAttemptId ? { submissionAttemptId: savedProgress.submissionAttemptId, submissionAttemptRevisionId: savedProgress.submissionAttemptRevisionId ?? before.id, ...(changed || invalidatedDocumentIds.length || savedProgress.submissionAttemptNeedsReconciliation ? { submissionAttemptNeedsReconciliation: true as const } : {}) } : {}) },
    invalidatedDocumentIds, removedDocumentIds, clearedQuestionIds, requiresStandardResign: changed };
}
